export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Matches the date/hour/minute components at the start of the string and
// ignores everything after them (seconds, a trailing `Z`, or a `+HH:MM`
// offset) — the API returns naive local wall-clock strings with no offset,
// and any offset that does show up must never be applied as a conversion.
const NAIVE_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/;

// The same leading components, without requiring a time to follow.
const NAIVE_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Formats a naive local wall-clock datetime string by reading its own
 * components — no `Date` arithmetic, no timezone conversion. `new Date(...)`
 * would silently shift the value the instant a `Z` or offset suffix appears,
 * which is exactly the bug this replaces (see
 * docs/shipment-integration-frontend.md §2).
 */
export function formatLocalDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const match = NAIVE_DATETIME.exec(value);
  if (!match) return '—';
  const [, year, month, day, hour, minute] = match;
  const monthName = MONTHS[Number(month) - 1];
  return `${day} ${monthName} ${year}, ${hour}:${minute}`;
}

/**
 * The date half of the same format, for columns where the time of day is
 * noise. It exists so no table falls back to `toLocaleDateString()`, which
 * prints `1/15/2026` next to this module's `15 Jan 2026` in the next column
 * and reads a day and a month the other way round depending on who is looking.
 */
export function formatLocalDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = NAIVE_DATE.exec(value);
  if (!match) return '—';
  const [, year, month, day] = match;
  return `${day} ${MONTHS[Number(month) - 1]} ${year}`;
}

export interface NaiveDate {
  year: number;
  month: number; // 1-12
  day: number;
}

/**
 * Reads the year/month/day components off a naive local wall-clock string,
 * for date-arithmetic callers (period bucketing, weekday calculation) that
 * need the numbers rather than a formatted string. Same rule as the
 * formatters above: no `new Date(value)`, no timezone conversion — arithmetic
 * on the result must go through `Date.UTC(year, month - 1, day)`.
 */
export function parseNaiveDate(value: string | null | undefined): NaiveDate | null {
  if (!value) return null;
  const match = NAIVE_DATE.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  return { year: Number(year), month: Number(month), day: Number(day) };
}

/**
 * Formats a real ISO 8601 UTC instant (`2026-10-02T09:15:00Z`) in the
 * viewer's own time zone, in the same `dd Mon yyyy, HH:mm` shape as
 * `formatLocalDateTime`.
 *
 * The one place `new Date(value)` is right: the supplier invoice endpoints
 * return true UTC timestamps (with the `Z`), not the naive local wall-clock
 * strings the rest of the API does, so here the conversion is the point. Never
 * use this for a naive string — it would shift it by the viewer's offset.
 */
export function formatUtcDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())} ${MONTHS[date.getMonth()]} ${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
